/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Forge Design Automation team for Inventor
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import Script from 'react-load-script';
import {connect} from 'react-redux';
import { getActiveProject, getDrawingsList, getActiveDrawing, downloadProgressShowing, downloadProgressTitle, downloadUrl, downloadFailedShowing, errorData } from '../reducers/mainReducer';
import { downloadDrawingFailedShowing } from '../reducers/mainReducer';
import { getDownloadLink } from '../actions/downloadActions';
import { showDownloadProgress, showDownloadFailed, showModalContact } from '../actions/uiFlagsActions';
import { fetchDrawingsList } from '../actions/drawingsListActions';
import ModalDownloadProgress from './modalDownloadProgress';
import ModalFail from './modalFail';
import './forgeView.css';
import Message from './message';
import repo from '../Repository';
import { viewerCss, viewerJs } from './shared';

let Autodesk = null;

export class ForgeView extends Component {

    constructor(props){
      super(props);

      this.viewerDiv = React.createRef();
      this.viewer = null;
    }

    handleScriptLoad() {

        const options = repo.hasAccessToken() ?
                { accessToken: repo.getAccessToken() } :
                { env: 'Local' };

        Autodesk = window.Autodesk;

        const container = this.viewerDiv.current;
        this.viewer = new Autodesk.Viewing.GuiViewer3D(container);

        // uncomment this for Viewer debugging
        //this.viewer.debugEvents(true);

        Autodesk.Viewing.Initializer(options, this.handleViewerInit.bind(this));
    }

    handleViewerInit() {
        const errorCode = this.viewer.start();
        if (errorCode)
            return;

        const customProfile = new Autodesk.Viewing.Profile({
            settings: {
                firstPersonToolPopup: false,
                bimWalkToolPopup: false
            },
            extensions: {
                unload: ['Autodesk.FullScreen', 'Autodesk.BimWalk']
            }
        });

        this.viewer.setProfile(customProfile);

        // orient camera in the same way as it's on the thumbnail
        // corresponding to ViewOrientationTypeEnum.kIsoTopRightViewOrientation
        const viewer = this.viewer;
        this.viewer.addEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT, (event) => {

            const viewCubeExtensionId = "Autodesk.ViewCubeUi";

            // this is not perfect, because the view transition is visible, so it's a place to improve someday
            if (event.extensionId === viewCubeExtensionId) {

                const viewCubeUI = event.target.getExtension(viewCubeExtensionId);
                viewCubeUI.setViewCube("front top right");

                viewer.removeEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT);
            }
        });

        this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, () => {

            if (viewer.settingsTools && viewer.settingsTools.fullscreenbutton)
            {
                viewer.settingsTools.fullscreenbutton.onClick = function ()
                {
                    if (!document.fullscreenElement)
                    {
                        document.documentElement.requestFullscreen();
                    }
                    else
                    {
                        if (document.exitFullscreen)
                        {
                            document.exitFullscreen();
                        }
                    }
                };
            }

            const props = this.props;
            const project = this.props.activeProject;

            if (project.type == "INVENTOR")
            {
                const comboButtonDownloads = new Autodesk.Viewing.UI.ComboButton('downloads-combobutton');
                comboButtonDownloads.icon.style = `background-image: url(${"icon-download.png"}); background-size: 32px;`;
                comboButtonDownloads.setToolTip('Downloads');


                const buttonSAT = new Autodesk.Viewing.UI.Button('download-sat-button');
                buttonSAT.icon.style = `background-image: url(${"icon-sat.png"}); background-size: 32px;`;
                buttonSAT.onClick = function () {
                    if (project?.id) {
                        props.getDownloadLink('CreateSATJob', project.id, project.hash, 'Preparing SAT');
                    }
                };
                buttonSAT.setToolTip('Download SAT');
                comboButtonDownloads.addControl(buttonSAT);

                const buttonRFA = new Autodesk.Viewing.UI.Button('download-rfa-button');
                buttonRFA.icon.style = `background-image: url(${"icon-rfa.png"}); background-size: 32px;`;
                buttonRFA.onClick = function () {
                    if (project?.id) {
                        props.getDownloadLink('CreateRFAJob', project.id, project.hash, 'Preparing RFA');
                    }
                };
                buttonRFA.setToolTip('Download RFA');
                comboButtonDownloads.addControl(buttonRFA);

                const buttonSTEP = new Autodesk.Viewing.UI.Button('download-step-button');
                buttonSTEP.icon.style = `background-image: url(${"icon-step.png"}); background-size: 32px;`;
                buttonSTEP.onClick = function () {
                    if (project?.id) {
                        props.getDownloadLink('CreateSTEPJob', project.id, project.hash, 'Preparing STEP');
                    }
                };
                buttonSTEP.setToolTip('Download STEP');
                comboButtonDownloads.addControl(buttonSTEP);

                const buttonDWG = new Autodesk.Viewing.UI.Button('download-dwg-button');
                buttonDWG.icon.style = `background-image: url(${"icon-dwg.png"}); background-size: 32px;`;
                buttonDWG.onClick = function () {
                    if (project?.id) {
                        props.getDownloadLink('CreateDrawingDownloadJob', project.id, project.hash, 'Preparing Drawings');
                    }
                };
                buttonDWG.setToolTip('Download DWG');
                comboButtonDownloads.addControl(buttonDWG);

                const buttonPDF = new Autodesk.Viewing.UI.Button('download-pdf-button');
                buttonPDF.icon.style = `background-image: url(${"icon-pdf.png"}); background-size: 32px;`;
                buttonPDF.onClick = function () {
                    if (project?.id) {
                        props.getDownloadLink('CreateDrawingPdfJob', project.id, project.hash, 'Preparing Drawing PDF', props.activeDrawing);
                    }
                };
                buttonPDF.setToolTip('Download PDF');
                comboButtonDownloads.addControl(buttonPDF);


                this.subToolbar = new Autodesk.Viewing.UI.ControlGroup('custom-download-toolbar');
                this.subToolbar.addControl(comboButtonDownloads);

                this.viewer.toolbar.addControl(this.subToolbar);
            }


            const buttonContact = new Autodesk.Viewing.UI.Button('contact-button');
            buttonContact.icon.style = `background-image: url(${"icon-contact.png"}); background-size: 32px;`;
            buttonContact.onClick = function () {
                if (project?.id) {
                    props.showModalContact(true);
                }
            };
            buttonContact.setToolTip('Contact');


            this.contactToolbar = new Autodesk.Viewing.UI.ControlGroup('custom-contact-toolbar');
            this.contactToolbar.addControl(buttonContact);

            this.viewer.toolbar.addControl(this.contactToolbar);

            viewer.removeEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT);
        });

        // skip loading of svf when there is no active project svf
        if (!this.props.activeProject.svf)
            return;

        Autodesk.Viewing.Document.load(
            this.getSvfUrl(), this.onDocumentLoadSuccess.bind(this), () => {}
        );
    }

    componentDidMount() {
        this.props.fetchDrawingsList(this.props.activeProject);
    }

    componentDidUpdate(prevProps) {
        if (Autodesk && (this.props.activeProject.svf !== prevProps.activeProject.svf)) {
            Autodesk.Viewing.Document.load(
                this.getSvfUrl(), this.onDocumentLoadSuccess.bind(this), () => {}
            );
        }

        // fetch drawings list when tab was active before projects initialized
        if (this.props.activeProject.id !== prevProps.activeProject.id)
            this.props.fetchDrawingsList(this.props.activeProject);
    }

    componentWillUnmount() {
        if (this.viewer) {
            this.viewer.finish();
            this.viewer = null;
            Autodesk.Viewing.shutdown();
        }
    }

    getSvfUrl() {
        return this.props.activeProject.svf + `/bubble.json`;
    }

    onDocumentLoadSuccess(viewerDocument) {
        this.props.fetchDrawingsList(this.props.activeProject);

        const defaultModel = viewerDocument.getRoot().getDefaultGeometry();
        this.viewer.loadDocumentNode(viewerDocument, defaultModel);
    }

    render() {
        const project = this.props.activeProject;

        return (
            <div className="modelContainer fullheight">
                <Message/>
                <div className="viewer" id="ForgeViewer">
                    <div ref={this.viewerDiv}></div>
                    <link rel="stylesheet" type="text/css" href={ viewerCss } />
                    <Script url={ viewerJs } onLoad={this.handleScriptLoad.bind(this)} />
                </div>
                {this.props.downloadProgressShowing &&
                    <ModalDownloadProgress
                        open={true}
                        title={this.props.downloadProgressTitle}
                        label={project.id}
                        icon='/Archive.svg'
                        onClose={() => this.props.showDownloadProgress(false)}
                        url={this.props.downloadUrl} />
                }
                {this.props.downloadFailedShowing &&
                    <ModalFail
                        open={true}
                        title={`${this.props.downloadProgressTitle} Failed`}
                        contentName="Project:"
                        label={project.id}
                        onClose={() => this.props.showDownloadFailed(false)}
                        errorData={this.props.errorData} />
                }
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(function (store) {
    const activeProject = getActiveProject(store);
    return {
        activeProject: activeProject,
        downloadProgressShowing: downloadProgressShowing(store),
        downloadProgressTitle: downloadProgressTitle(store),
        downloadFailedShowing: downloadFailedShowing(store),
        downloadUrl: downloadUrl(store),
        errorData: errorData(store),
        drawingDownloadFailedShowing: downloadDrawingFailedShowing(store),
        activeDrawing: getActiveDrawing(store),
        drawingsList: getDrawingsList(store)
    };
}, { ForgeView, getDownloadLink, fetchDrawingsList, showDownloadProgress, showDownloadFailed, showModalContact })(ForgeView);